/**
 * RichText Settings - использует переиспользуемые компоненты настроек
 */
import React from 'react';
import Icon from '../../components/AppIcon';
import {
  SettingsGroup,
  SelectFieldSettings,
} from '../components/settings';

const BLOCK_TYPES = [
  { type: 'h2', label: 'Заголовок H2', icon: 'Heading2' },
  { type: 'h3', label: 'Заголовок H3', icon: 'Heading3' },
  { type: 'paragraph', label: 'Параграф', icon: 'AlignLeft' },
  { type: 'bullet-list', label: 'Маркированный список', icon: 'List' },
  { type: 'numbered-list', label: 'Нумерованный список', icon: 'ListOrdered' },
  { type: 'quote', label: 'Цитата', icon: 'Quote' },
  { type: 'divider', label: 'Разделитель', icon: 'Minus' }
];

export default function RichTextSettings({ data, onChange }) {
  const blocks = data?.blocks || [];

  const updateData = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const updateBlocks = (newBlocks) => {
    updateData('blocks', newBlocks);
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      content: '',
      items: type.includes('list') ? [''] : undefined
    };
    updateBlocks([...blocks, newBlock]);
  };

  const updateBlock = (blockId, field, value) => {
    const newBlocks = blocks.map(block =>
      block.id === blockId ? { ...block, [field]: value } : block
    );
    updateBlocks(newBlocks);
  };

  const removeBlock = (blockId) => {
    updateBlocks(blocks.filter(block => block.id !== blockId));
  };

  const moveBlock = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;

    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    updateBlocks(newBlocks);
  };

  // Список: добавить пункт
  const addListItem = (blockId) => {
    const newBlocks = blocks.map(block => {
      if (block.id === blockId) {
        return { ...block, items: [...(block.items || []), { text: '', details: '' }] };
      }
      return block;
    });
    updateBlocks(newBlocks);
  };

  // Список: обновить пункт
  const updateListItem = (blockId, itemIndex, field, value) => {
    const newBlocks = blocks.map(block => {
      if (block.id === blockId) {
        const newItems = [...(block.items || [])];
        const currentItem = newItems[itemIndex];
        // Поддержка старого формата (строка) и нового (объект)
        if (typeof currentItem === 'string') {
          newItems[itemIndex] = { text: field === 'text' ? value : currentItem, details: field === 'details' ? value : '' };
        } else {
          newItems[itemIndex] = { ...currentItem, [field]: value };
        }
        return { ...block, items: newItems };
      }
      return block;
    });
    updateBlocks(newBlocks);
  };

  // Список: удалить пункт
  const removeListItem = (blockId, itemIndex) => {
    const newBlocks = blocks.map(block => {
      if (block.id === blockId) {
        const newItems = (block.items || []).filter((_, i) => i !== itemIndex);
        return { ...block, items: newItems.length > 0 ? newItems : [{ text: '', details: '' }] };
      }
      return block;
    });
    updateBlocks(newBlocks);
  };

  const getBlockIcon = (type) => {
    return BLOCK_TYPES.find(bt => bt.type === type)?.icon || 'FileText';
  };

  const getBlockLabel = (type) => {
    return BLOCK_TYPES.find(bt => bt.type === type)?.label || type;
  };

  const renderBlockEditor = (block, index) => {
    const isTextBlock = ['h2', 'h3', 'paragraph', 'quote'].includes(block.type);
    const isListBlock = ['bullet-list', 'numbered-list'].includes(block.type);
    const isDivider = block.type === 'divider';

    return (
      <div key={block.id} className="bg-muted rounded-lg p-3 space-y-2">
        {/* Заголовок блока */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Icon name={getBlockIcon(block.type)} size={14} />
            <span>{getBlockLabel(block.type)}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => moveBlock(index, -1)}
              disabled={index === 0}
              className="p-1 hover:bg-background rounded disabled:opacity-30"
            >
              <Icon name="ChevronUp" size={14} />
            </button>
            <button
              onClick={() => moveBlock(index, 1)}
              disabled={index === blocks.length - 1}
              className="p-1 hover:bg-background rounded disabled:opacity-30"
            >
              <Icon name="ChevronDown" size={14} />
            </button>
            <button
              onClick={() => removeBlock(block.id)}
              className="p-1 hover:bg-error/10 hover:text-error rounded"
            >
              <Icon name="Trash2" size={14} />
            </button>
          </div>
        </div>

        {/* Контент блока */}
        {isTextBlock && (
          <textarea
            value={block.content || ''}
            onChange={(e) => updateBlock(block.id, 'content', e.target.value)}
            placeholder={block.type === 'quote' ? 'Текст цитаты...' : 'Введите текст...'}
            rows={block.type === 'paragraph' ? 3 : 1}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none text-base md:text-sm"
          />
        )}

        {isListBlock && (
          <div className="space-y-2">
            {(block.items || []).map((item, itemIndex) => {
              const text = typeof item === 'string' ? item : item?.text || '';
              const details = typeof item === 'string' ? '' : item?.details || '';
              return (
                <div key={itemIndex} className="bg-background rounded-lg p-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-primary text-xs w-5 font-medium">
                      {block.type === 'numbered-list' ? `${itemIndex + 1}.` : '•'}
                    </span>
                    <input
                      type="text"
                      value={text}
                      onChange={(e) => updateListItem(block.id, itemIndex, 'text', e.target.value)}
                      placeholder="Текст пункта..."
                      className="flex-1 px-2 py-1.5 border border-border rounded-lg bg-background text-foreground placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/50 text-base md:text-sm"
                    />
                    <button
                      onClick={() => removeListItem(block.id, itemIndex)}
                      className="p-1 text-text-secondary hover:text-error"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                  <div className="pl-7">
                    <textarea
                      value={details}
                      onChange={(e) => updateListItem(block.id, itemIndex, 'details', e.target.value)}
                      placeholder="Подробности..."
                      rows={1}
                      className="w-full px-2 py-1 border border-border rounded-lg bg-background text-foreground placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/50 text-base md:text-xs resize-none"
                    />
                  </div>
                </div>
              );
            })}
            <button
              onClick={() => addListItem(block.id)}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
            >
              <Icon name="Plus" size={12} />
              Добавить пункт
            </button>
          </div>
        )}

        {isDivider && (
          <div className="py-1">
            <hr className="border-border" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Контент */}
      <SettingsGroup title={`Контент (${blocks.length})`}>
        <div className="space-y-2">
          {blocks.map((block, index) => renderBlockEditor(block, index))}
        </div>

        {blocks.length === 0 && (
          <p className="text-sm text-text-secondary text-center py-4">
            Нет блоков. Добавьте первый блок ниже.
          </p>
        )}

        {/* Добавить блок */}
        <div className="pt-2">
          <p className="text-xs font-medium mb-2 text-text-secondary">Добавить блок:</p>
          <div className="grid grid-cols-2 gap-1.5">
            {BLOCK_TYPES.map((blockType) => (
              <button
                key={blockType.type}
                onClick={() => addBlock(blockType.type)}
                className="flex items-center gap-2 px-2 py-1.5 border border-border rounded-lg hover:border-primary/50 hover:bg-muted transition-colors text-xs"
              >
                <Icon name={blockType.icon} size={14} className="text-text-secondary" />
                <span>{blockType.label}</span>
              </button>
            ))}
          </div>
        </div>
      </SettingsGroup>

      {/* Оформление */}
      <SettingsGroup title="Оформление">
        <SelectFieldSettings
          label="Ширина контента"
          value={data?.maxWidth || 'md'}
          onChange={(value) => updateData('maxWidth', value)}
          mode="grid"
          columns={4}
          options={[
            { value: 'sm', label: 'Узкая' },
            { value: 'md', label: 'Средняя' },
            { value: 'lg', label: 'Широкая' },
            { value: 'full', label: 'Полная' },
          ]}
        />

        <SelectFieldSettings
          label="Выравнивание"
          value={data?.alignment || 'left'}
          onChange={(value) => updateData('alignment', value)}
          mode="grid"
          columns={2}
          options={[
            { value: 'left', label: 'Слева', icon: 'AlignLeft' },
            { value: 'center', label: 'Центр', icon: 'AlignCenter' },
          ]}
        />
      </SettingsGroup>
    </div>
  );
}
